import {DumpsterFire} from "../DumpsterFire";
import {DumpsterFireGameMode} from "../server/DumpsterFireGameMode";
import {TimelineLite} from "gsap";
import {AlfaSlabOne} from "../Fonts";
import {DumpsterFireSound} from "../sound/DumpsterFireSound";
import {CustomEase} from "gsap/dist/CustomEase";
import {StageLayer} from "@nolimitcity/slot-keypad/bin/core/app/StageLayer";
import {ContinueButton} from "@nolimitcity/slot-keypad/bin/core/app/gui/buttons/concretebuttons/ContinueButton";
import {ScreenBounds} from "@nolimitcity/slot-keypad/bin/core/app/display/ScreenBounds";
import {NolimitApplication} from "@nolimitcity/slot-keypad/bin/core/app/NolimitApplication";


export class DFFSIntroView extends PIXI.Container implements StageLayer {

    private _introTexture: PIXI.Sprite;
    private _introSpine: PIXI.spine.Spine;
    private _button: ContinueButton;
    private _button2: ContinueButton;
    private _introText: PIXI.Text;
    private _fsText: PIXI.Text;
    private _clickCallback: () => void;
    private _wheel: PIXI.spine.Spine;
    private _firstArrow: PIXI.Sprite;
    private _secondArrow: PIXI.Sprite;
    private _thirdArrow: PIXI.Sprite;
    private _upgradeBase: PIXI.Sprite;
    private _bomb: PIXI.Sprite;
    private _xWays: PIXI.Sprite;
    private _shot: PIXI.Sprite;
    private _dogAnim: PIXI.spine.Spine;

    constructor() {
        super();
        this.name = "DFFSIntroView";
        this.init();
    }

    public onResize(): void {
        const bounds: ScreenBounds = NolimitApplication.screenBounds;
        if (NolimitApplication.isLandscape) {
            this._button.position.set(360, 615);
            this._button2.position.set(360, 615);
        } else {
            const centerBottom = (bounds.bottom - 610) * 0.5 + 610;
            this._button.position.set(360, centerBottom - 80);
            this._button2.position.set(360, 615);
        }
    }

    public spinWheel(): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        const totalRotation: number = (360) * 5 + (DumpsterFire.model.data.luckyWheelStop - 60); // 3 full rotations in radians (3 * 360 degrees) and final radian to stop at.
        let duration: number = DumpsterFire.model.data.upgradedSymbols.length === 3 ? 0 : 5;
        tl.add(() => {
            this._wheel.state.clearTracks();
            this._wheel.state.setAnimation(0, "static", false);
            this._wheel.pivot.set(6.2, -1.2);
        }, 0);
        tl.to(this._wheel, {
            angle: totalRotation,
            duration: duration,
            ease: CustomEase.create("custom", "M0,0 C0.17,0 0.244,0.05 0.297,0.11 0.358,0.181 0.404,0.324 0.466,0.517 0.524,0.698 0.576,0.841 0.624,0.893 0.663,0.936 0.794,1 1,1 ")
        }, 0);
        if (duration !== 0) {
            tl.add(() => {
                DumpsterFire.soundPlayer.playSound(DumpsterFireSound.FS_INTRO_WHEEL)
            }, 0)
        }
        return tl;
    }

    private symName(): string {
        let skinName: string = "default";
        let upgradedSymbols: number = DumpsterFire.model.data.upgradedSymbols.length;
        if (upgradedSymbols === 1) {
            skinName = "duckHuntSpinsFlag";
        } else if (upgradedSymbols === 2) {
            skinName = "hawkEyeSpinsFlag";
        } else if (upgradedSymbols === 3) {
            skinName = "bigGameSpinsFlag";
        }
        return skinName;
    }

    private playCarAnim(spine: PIXI.spine.Spine): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        let animSkinName: string = this.symName();
        spine.skeleton.setSkinByName(animSkinName);
        tl.add(DumpsterFire.playSpineAnimationFromParams({
            spineItem: spine,
            animation: "intro",
            skin: animSkinName
        }), 0);
        tl.add(DumpsterFire.playSpineAnimationFromParams({
            spineItem: spine,
            animation: "loop",
            skin: animSkinName,
            playLoop: true
        }), 1);
        return tl
    }

    private playCarExitAnim(spine: PIXI.spine.Spine): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        let animSkinName: string = this.symName();
        spine.skeleton.setSkinByName(animSkinName);
        tl.add(DumpsterFire.playSpineAnimationFromParams({
            spineItem: spine,
            animation: "outro",
            skin: animSkinName,
            endTime: 0.5
        }), 0);
        tl.add(() => {
            spine.visible = false;
        });
        return tl
    }

    public showIntro(callback: () => void): TimelineLite {
        DumpsterFire.model.isFreespin = true;
        DumpsterFire.backgroundElements.setIdleTimeline();
        const showIntroTL: TimelineLite = new TimelineLite();
        showIntroTL.add([() => {
            this._introSpine.visible = false;
            DumpsterFire.blackBg.showBlackBg();
            this._introText.visible = true;
            this._fsText.visible = true;
            let name: string = "";
            if (DumpsterFire.model.data.upgradedSymbols.length == 1) {
                name = DumpsterFire.apiPlugin.translations.translate("DUCK HUNT" + "\n" + "    SPINS");
            } else if (DumpsterFire.model.data.upgradedSymbols.length == 2) {
                name = DumpsterFire.apiPlugin.translations.translate("HAWK EYE" + "\n" + "     SPINS");
            } else if (DumpsterFire.model.data.upgradedSymbols.length == 3) {
                name = DumpsterFire.apiPlugin.translations.translate("BIG GAME" + "\n" + "     SPINS");
            }
            this._introText.text = DumpsterFire.model.gameData.freespinsLeft.toString();
            this._fsText.text = name;

            this._clickCallback = callback;
            this._button.alpha = 0;
            this._button.visible = true;
            DumpsterFire.soundPlayer.switchMusic("FS_INTRO_MUSIC");
            DumpsterFire.soundPlayer.playSound(DumpsterFireSound.FS_INTRO_START);
        },
            DumpsterFire.reelAnticipation.reset(false)
        ], 0);
        showIntroTL.to([this._button, this._introTexture], {
            duration: 0.5,
            alpha: 1
        }, 0);
        showIntroTL.add([() => {
            DumpsterFire.soundPlayer.playSound(DumpsterFireSound.FS_INTRO_CAR, Number.MAX_VALUE);
            this._introSpine.visible = true;
        },
            this.playCarAnim(this._introSpine)], 0);
        showIntroTL.add(() => {
            this._button.enable(true);
        }, 1);
        return showIntroTL;
    }

    public showUpgrades(callback: () => void): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        const timelineDur: number = DumpsterFire.model.data.upgradedSymbols.length === 3 ? 2.0 : 7.5;
        tl.add(() => {
            this._wheel.state.setEmptyAnimation(0, 0);
            this._wheel.skeleton.setToSetupPose();
            this._wheel.skeleton.setSlotsToSetupPose();
        }, 0);
        tl.to([this._introTexture, this._button2], {
            duration: 0.2,
            alpha: 1
        }, 0);
        tl.add(() => {
            this.addArrows();
            this._introTexture.visible = true;
            this._wheel.visible = true;
            this._dogAnim.visible = true;
            this._wheel.state.setAnimation(0, "static", false);
            this._wheel.state.setAnimation(1, "static", false);
            this._wheel.state.setAnimation(2, "static", false);
            this._dogAnim.state.setAnimation(0, "animation", true);
        }, 0);
        tl.add(this.spinWheel(), 0.5);
        tl.add([this.addGlow(),
            () => {
                DumpsterFire.soundPlayer.playSound(DumpsterFireSound.FS_INTRO_WHEEL_BLINK, Number.MAX_VALUE);
            }]);
        tl.add(() => {
            DumpsterFire.soundPlayer.playSound(DumpsterFireSound.FS_INTRO_UPGRADES_SIGN);
            this._wheel.state.clearTracks();
            this._wheel.visible = false;
            this._wheel.rotation = 0;
            this._firstArrow.visible = false
            this._secondArrow.visible = false
            this._thirdArrow.visible = false
            this._upgradeBase.visible = true;
            this.addUpgrades();
            this._button2.alpha = 0;
            this._button2.visible = true;
            this._clickCallback = callback;
        }, `${timelineDur - 0.1}`);
        tl.addLabel("_end");
        tl.add(() => {
            document.removeEventListener('pointerdown', onGlobalClick);
            this._button2.enable(true);
        }, `${timelineDur}`);

        // Add global click listener to skip to the end of the timeline
        const onGlobalClick: () => void = (): void => {
            tl.seek("_end");
            DumpsterFire.soundPlayer.stopSound(DumpsterFireSound.FS_INTRO_WHEEL_BLINK);
            DumpsterFire.soundPlayer.stopSound(DumpsterFireSound.FS_INTRO_WHEEL)
            // Optionally, you can remove the pointerdown listener after it has been triggered
            document.removeEventListener('pointerdown', onGlobalClick);
        };
        document.addEventListener('pointerdown', onGlobalClick);
        return tl;
    }

    protected setNextModeBg(): void {
        DumpsterFire.backgroundElements.setMode(DumpsterFireGameMode.FREESPIN);
        DumpsterFire.gameLogo.setMode(DumpsterFireGameMode.FREESPIN);
    }

    private init(): void {
        this._introSpine = new PIXI.spine.Spine(DumpsterFire.assetLoader.getSpineAsset("fsIntro"));
        this._introSpine.position.set(324, 360);
        this._introSpine.visible = false;

        this._introTexture = new PIXI.Sprite(DumpsterFire.assetLoader.getTextures("wheelBase")[0]);
        this._introTexture.position.set(363, 360);
        this._introTexture.anchor.set(0.5, 0.5);
        this._introTexture.visible = false;
        this._introText = new PIXI.Text("", {
            fontFamily: AlfaSlabOne.FAMILY,
            fontSize: 92,
            fontWeight: "Bold",
            fill: "#F7F4C5"
        });
        this._introText.position.set(500, 150);
        this._introText.anchor.set(0.5, 0.5);
        this._introText.visible = false;

        this._fsText = new PIXI.Text("", {
            fontFamily: AlfaSlabOne.FAMILY,
            fontSize: 49,
            fontWeight: "Bold",
            fill: "#304352"
        });
        this._fsText.position.set(520, 310);
        this._fsText.anchor.set(0.5, 0.5);
        this._fsText.angle = -7
        this._fsText.visible = false;

        this._button = new ContinueButton();
        this._button.addClickCallback(() => {
            this.onButtonClick();
        });
        this._button.pivot.set(this._button.width * 0.5, this._button.height * 0.5);
        this._button.enable(false)
        this._button.visible = false;

        this._button2 = new ContinueButton();
        this._button2.addClickCallback(() => {
            this.onButton2Click();
        });
        this._button2.pivot.set(this._button.width * 0.5, this._button.height * 0.5);
        this._button2.enable(false)
        this._button2.visible = false;

        this._wheel = new PIXI.spine.Spine(DumpsterFire.assetLoader.getSpineAsset("spinWheel"));
        this._wheel.position.set(350, 310);
        this._wheel.pivot.set(6.2, -1.2);
        this._wheel.visible = false;

        this._dogAnim = new PIXI.spine.Spine(DumpsterFire.assetLoader.getSpineAsset("spinWheel_dog"));
        this._dogAnim.position.set(650, 600);
        this._dogAnim.visible = false;

        this._firstArrow = new PIXI.Sprite(DumpsterFire.assetLoader.getTextures("arrow")[0]);
        this._firstArrow.position.set(318, 30);
        this._firstArrow.visible = false;

        this._secondArrow = new PIXI.Sprite(DumpsterFire.assetLoader.getTextures("arrow")[0]);
        this._secondArrow.position.set(130, 470);
        this._secondArrow.angle = -120
        this._secondArrow.visible = false;

        this._thirdArrow = new PIXI.Sprite(DumpsterFire.assetLoader.getTextures("arrow")[0]);
        this._thirdArrow.position.set(600, 420);
        this._thirdArrow.angle = 120
        this._thirdArrow.visible = false;

        this._upgradeBase = new PIXI.Sprite(DumpsterFire.assetLoader.getTextures("upgradeBase")[0]);
        this._upgradeBase.position.set(80, 80);
        this._upgradeBase.visible = false;

        this._bomb = new PIXI.Sprite(DumpsterFire.assetLoader.getTextures("bomb")[0]);
        this._bomb.position.set(180, 245);
        this._bomb.visible = false;

        this._xWays = new PIXI.Sprite(DumpsterFire.assetLoader.getTextures("xways")[0]);
        this._xWays.position.set(185, 137);
        this._xWays.visible = false;

        this._shot = new PIXI.Sprite(DumpsterFire.assetLoader.getTextures("shot")[0]);
        this._shot.position.set(180, 344);
        this._shot.visible = false;

        this.onResize();

        this.addChild(this._introSpine, this._introTexture, this._wheel, this._dogAnim, this._button, this._firstArrow, this._secondArrow, this._thirdArrow, this._upgradeBase, this._bomb, this._shot, this._xWays, this._button2);
    }

    public onButton2Click(): TimelineLite {
        this._button2.enable(false);
        const hideIntroTL: TimelineLite = new TimelineLite(),
            duration: number = 0.2;
        hideIntroTL.to([this._button2], {
            duration: duration,
            alpha: 0
        }, 0);
        hideIntroTL.add([
            () => {
                DumpsterFire.model.calculatePrevMultiPos();
                DumpsterFire.soundPlayer.stopSound(DumpsterFireSound.FS_INTRO_WHEEL_BLINK);
                DumpsterFire.soundPlayer.switchMusic(DumpsterFire.model.data.nextMode);
                if (DumpsterFire.model.isRestore) {
                    DumpsterFire.keypadPlugin.setZeroBetSpinCounter(Math.max(DumpsterFire.model.gameData.freespinsLeft, 0));
                } else {
                    DumpsterFire.keypadPlugin.setZeroBetSpinCounter(DumpsterFire.model.gameData.freespinsLeft);
                }
                this._dogAnim.visible = false;
                this._dogAnim.state.clearTracks();
                this._dogAnim.state.setEmptyAnimation(0, 0);
            }], 0);
        hideIntroTL.to([this._introTexture], {
            duration: duration,
            alpha: 0
        }, 0);
        hideIntroTL.add(() => {
            this._button2.visible = false;
            this._button2.alpha = 0;
            this._introTexture.visible = false;
            DumpsterFire.backgroundElements.activeUpgradedSymbol();
            this.setNextModeBg();
            DumpsterFire.blackBg.onComplete();
            this._upgradeBase.visible = false
            this._bomb.visible = false
            this._xWays.visible = false
            this._shot.visible = false
            this._clickCallback();
        }, '>');
        return hideIntroTL;
    }

    private onButtonClick(): void {
        this._button.enable(false);
        const hideIntroTL: TimelineLite = new TimelineLite(),
            duration: number = 0.2;
        hideIntroTL.to([this._button], {
            duration: duration,
            alpha: 0
        }, 0);
        hideIntroTL.add([() => {
            DumpsterFire.soundPlayer.playSound(DumpsterFireSound.FS_CONTINUE);
            DumpsterFire.soundPlayer.stopSound(DumpsterFireSound.FS_INTRO_CAR);
            this._introSpine.visible = false;
            this._button.visible = false;
            this._introText.visible = false;
            this._fsText.visible = false;
        }], 0);
        hideIntroTL.add([() => {
            this._introSpine.visible = true;
        },
            this.playCarExitAnim(this._introSpine)], 0);
        hideIntroTL.add([() => {
            this.addArrows();
            this._introSpine.visible = false;
            this._introSpine.state.setEmptyAnimation(0, 0);
            this._introSpine.state.clearTracks();
        }], 0.8);
        hideIntroTL.add(() => {
            this._clickCallback();
        }, 0.8);
    }

    private addUpgrades(): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        DumpsterFire.soundPlayer.stopSound(DumpsterFireSound.FS_INTRO_WHEEL_BLINK);
        DumpsterFire.model.data.upgradedSymbols.forEach(symbol => {
            if (symbol === "S") {
                this._shot.visible = true;
            }
            if (symbol === "XW") {
                this._xWays.visible = true;
            }
            if (symbol === "XB") {
                this._bomb.visible = true;
            }
        })
        return tl;
    }

    private addGlow(): TimelineLite {
        const tl: TimelineLite = new TimelineLite();
        DumpsterFire.model.data.upgradedSymbols.forEach(symbol => {
            if (symbol === "S") {
                tl.add(() => {
                    this._wheel.state.setAnimation(0, "scatterGlow", true);
                });
            }
            if (symbol === "XW") {
                tl.add(() => {
                    this._wheel.state.setAnimation(1, "xWaysInfectiousGlow", true);
                });
            }
            if (symbol === "XB") {
                tl.add(() => {
                    this._wheel.state.setAnimation(2, "xBombGlow", true);
                });
            }
        })
        return tl;
    }

    private addArrows() {
        if (DumpsterFire.model.data.upgradedSymbols.length === 1) {
            this._firstArrow.visible = true
        } else if (DumpsterFire.model.data.upgradedSymbols.length === 2) {
            this._firstArrow.visible = true
            this._secondArrow.visible = true
        } else if (DumpsterFire.model.data.upgradedSymbols.length === 3) {
            this._firstArrow.visible = true
            this._secondArrow.visible = true
            this._thirdArrow.visible = true
        }
    }
}